import React, { useState, useEffect } from "react";
import ModalBase from "./ModalBase";
import { useFeedback } from "@contexts/FeedbackContext";
import {
  HiClipboardList,
  HiPaperAirplane,
  HiExclamation,
} from "react-icons/hi";
import {
  FornecedorEnvioBloco,
  ItemEnvioFornecedor,
  ModoEnvioFornecedor,
  StatusOrcamento,
} from "@models/index";

interface FornecedorFormData extends FornecedorEnvioBloco {
  email: string;
  telefone: string;
  selecionado: boolean;
  modo: ModoEnvioFornecedor;
  emailError?: string;
}

interface ModalEnvioFornecedorProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (
    fornecedores: Array<{
      fornecedorId: string;
      email: string;
      telefone: string;
      modo: ModoEnvioFornecedor;
    }>,
  ) => Promise<void>;
  fornecedores: FornecedorEnvioBloco[];
}

const STATUS_BADGE: Record<string, { label: string; classe: string }> = {
  [StatusOrcamento.AGUARDANDO_ENVIO]: {
    label: "aguardando envio",
    classe: "badge-ghost",
  },
  aguardando: { label: "aguardando envio", classe: "badge-ghost" },
  [StatusOrcamento.ENVIADA]: { label: "enviada", classe: "badge-info" },
  [StatusOrcamento.REENVIADA]: { label: "reenviada", classe: "badge-info" },
  [StatusOrcamento.RESPONDIDA]: {
    label: "respondida",
    classe: "badge-success",
  },
  [StatusOrcamento.REJEITADA]: { label: "recusada", classe: "badge-error" },
};

const badgeDoItem = (item: ItemEnvioFornecedor) =>
  STATUS_BADGE[item.status] ?? STATUS_BADGE[StatusOrcamento.AGUARDANDO_ENVIO];

/** Itens que serão enviados de fato, conforme o modo escolhido. */
const itensDoModo = (
  fornecedor: FornecedorFormData,
): ItemEnvioFornecedor[] =>
  fornecedor.modo === "todos"
    ? fornecedor.itens
    : fornecedor.itens.filter((i) => i.pendente);

const ModalEnvioFornecedor: React.FC<ModalEnvioFornecedorProps> = ({
  isOpen,
  onClose,
  onSend,
  fornecedores: fornecedoresIniciais,
}) => {
  const [fornecedores, setFornecedores] = useState<FornecedorFormData[]>([]);
  const [sending, setSending] = useState(false);
  const { showError, showWarning } = useFeedback();

  // Atualizar sempre que a modal abrir OU quando fornecedoresIniciais mudarem
  useEffect(() => {
    if (isOpen && fornecedoresIniciais && fornecedoresIniciais.length > 0) {
      setFornecedores(
        fornecedoresIniciais.map((f) => ({
          ...f,
          email: f.email || "",
          telefone: f.telefone || "",
          selecionado: false,
          // Padrão é o pedido completo: mandar só o item novo faria a API criar
          // um segundo orçamento para o mesmo fornecedor — outro link, outro PDF.
          modo: "todos" as ModoEnvioFornecedor,
          emailError: "",
        })),
      );
    }
  }, [isOpen, fornecedoresIniciais]);

  // Limpar estado ao fechar
  useEffect(() => {
    if (!isOpen) {
      setFornecedores([]);
      setSending(false);
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    // Remove espaços em branco e valida formato de e-mail
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return false;

    // Rejeitar e-mails com vírgulas ou outros caracteres inválidos
    if (/[,;\s<>\[\]\(\)\\"']/.test(trimmedEmail)) {
      return false;
    }

    // Regex robusta para validação de e-mail
    // Formato: usuario@dominio.extensao
    const emailRegex =
      /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
    return emailRegex.test(trimmedEmail);
  };

  const atualizarFornecedor = (
    index: number,
    patch: Partial<FornecedorFormData>,
  ) => {
    setFornecedores((atual) =>
      atual.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    );
  };

  const handleToggleFornecedor = (index: number) => {
    atualizarFornecedor(index, {
      selecionado: !fornecedores[index].selecionado,
      emailError: "",
    });
  };

  const handleSend = async () => {
    const fornecedoresSelecionados = fornecedores.filter((f) => f.selecionado);

    if (fornecedoresSelecionados.length === 0) {
      showWarning(
        "Selecione pelo menos um fornecedor para enviar a solicitação.",
      );
      return;
    }

    // Validação - trim para remover espaços
    let hasErrors = false;
    const updated = fornecedores.map((f) => {
      if (!f.selecionado) return f;

      let emailError = "";
      const trimmedEmail = f.email.trim();

      if (!trimmedEmail) {
        emailError = "E-mail obrigatório";
        hasErrors = true;
      } else if (!validateEmail(trimmedEmail)) {
        emailError = "E-mail inválido (exemplo: usuario@dominio.com)";
        hasErrors = true;
      }

      return { ...f, email: trimmedEmail, emailError };
    });

    if (hasErrors) {
      setFornecedores(updated);
      return;
    }

    setSending(true);

    try {
      await onSend(
        fornecedoresSelecionados.map((f) => ({
          fornecedorId: f.id,
          email: f.email.trim(), // Garantir trim no envio
          telefone: f.telefone,
          modo: f.modo,
        })),
      );
      onClose();
    } catch (error) {
      console.error("Erro ao enviar solicitações:", error);
      showError("Erro ao enviar solicitações de orçamento. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  const selecionados = fornecedores.filter((f) => f.selecionado);
  // Reenvio é o que exige atenção: reabre a cotação de itens já respondidos.
  const totalReenvios = selecionados.filter((f) =>
    itensDoModo(f).some((i) => !i.pendente),
  ).length;

  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <div className="pb-6 border-b border-base-300 mb-6">
        <h2 className="text-xl font-bold mb-2 text-base-content flex items-center gap-2">
          <HiClipboardList className="h-5 w-5" />
          Solicitação de orçamento por fornecedor
        </h2>
        <p className="text-sm text-base-content/70">
          Selecione os fornecedores e informe o e-mail para cada um
        </p>
      </div>

      <div>
        <p className="text-sm text-base-content/70 mb-4">
          Marque os fornecedores que devem receber a solicitação de orçamento e
          informe/atualize o e-mail. Se você incluiu ou excluiu itens, reenvie o
          pedido completo para que o fornecedor cote tudo de uma vez.
        </p>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {fornecedores.map((fornecedor, index) => {
            const pendentes = fornecedor.itens.filter((i) => i.pendente);
            const jaSolicitados = fornecedor.itens.filter((i) => !i.pendente);
            const somenteReenvio = pendentes.length === 0;
            const itensQueSerao = itensDoModo(fornecedor);
            const reabreCotacoes = itensQueSerao.some((i) => !i.pendente);

            return (
              <div
                key={fornecedor.id}
                className="p-4 border border-base-300 rounded-lg bg-base-50 hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id={`forn-${index}`}
                    className="checkbox checkbox-primary"
                    checked={fornecedor.selecionado}
                    onChange={() => handleToggleFornecedor(index)}
                    disabled={sending}
                  />
                  <label
                    htmlFor={`forn-${index}`}
                    className="cursor-pointer flex-1"
                  >
                    <strong className="text-base-content">
                      {fornecedor.nome}
                    </strong>
                    <span className="text-base-content/60 ml-2 text-sm">
                      ({fornecedor.itens.length}{" "}
                      {fornecedor.itens.length === 1 ? "item" : "itens"}
                      {/* O detalhe só ajuda quando há as duas situações; se é
                          tudo pendente ou tudo já solicitado, o total basta. */}
                      {jaSolicitados.length > 0 &&
                        pendentes.length > 0 &&
                        ` · ${pendentes.length} pendente${
                          pendentes.length === 1 ? "" : "s"
                        }, ${jaSolicitados.length} já solicitado${
                          jaSolicitados.length === 1 ? "" : "s"
                        }`}
                      )
                    </span>
                    {somenteReenvio && (
                      <span className="badge badge-warning badge-sm ml-2">
                        reenvio
                      </span>
                    )}
                  </label>
                </div>

                {fornecedor.selecionado && (
                  <>
                    <div className="mb-4 p-3 bg-base-200 rounded-lg">
                      <div className="text-xs text-base-content/70 mb-2">
                        Itens:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {fornecedor.itens.map((item) => {
                          const badge = badgeDoItem(item);
                          const vai = itensQueSerao.includes(item);
                          return (
                            <div
                              key={item.itemId || item.numero}
                              className={`badge badge-outline gap-1 text-xs${
                                vai ? "" : " opacity-40 line-through"
                              }`}
                              title={
                                vai
                                  ? undefined
                                  : "Não será incluído neste envio"
                              }
                            >
                              {item.numero ? `${item.numero} — ` : ""}
                              {item.nome}
                              <span
                                className={`badge badge-xs ${badge.classe}`}
                              >
                                {badge.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Só há escolha quando o fornecedor tem as duas coisas:
                        item que nunca saiu e item já solicitado. */}
                    {jaSolicitados.length > 0 && pendentes.length > 0 && (
                      <div className="form-control mb-4">
                        <span className="label-text font-medium mb-2">
                          O que enviar
                        </span>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              className="radio radio-primary radio-sm mt-0.5"
                              name={`modo-${fornecedor.id}`}
                              checked={fornecedor.modo === "todos"}
                              onChange={() =>
                                atualizarFornecedor(index, { modo: "todos" })
                              }
                              disabled={sending}
                            />
                            <span className="text-sm">
                              <strong>
                                Reenviar todos os {fornecedor.itens.length}{" "}
                                itens
                              </strong>
                              <span className="block text-xs text-base-content/60">
                                Um único link e um único PDF, com todos os itens
                                abertos para cotação.
                              </span>
                            </span>
                          </label>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              className="radio radio-primary radio-sm mt-0.5"
                              name={`modo-${fornecedor.id}`}
                              checked={fornecedor.modo === "pendentes"}
                              onChange={() =>
                                atualizarFornecedor(index, {
                                  modo: "pendentes",
                                })
                              }
                              disabled={sending}
                            />
                            <span className="text-sm">
                              <strong>
                                Enviar somente {pendentes.length} item(ns)
                                pendente(s)
                              </strong>
                              <span className="block text-xs text-base-content/60">
                                Não mexe no que já foi cotado, mas cria um
                                segundo orçamento para este fornecedor.
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    )}

                    {reabreCotacoes && (
                      <div className="alert alert-warning py-2 mb-4 text-sm">
                        <HiExclamation className="h-5 w-5 shrink-0" />
                        <span>
                          Reenvio: os valores já cotados voltam a ser editáveis
                          e o fornecedor precisará assinar e enviar o documento
                          novamente.
                        </span>
                      </div>
                    )}

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">E-mail *</span>
                      </label>
                      <input
                        id={`email-${index}`}
                        type="email"
                        className={`input input-bordered ${
                          fornecedor.emailError ? "input-error" : ""
                        }`}
                        value={fornecedor.email}
                        onChange={(e) =>
                          atualizarFornecedor(index, {
                            email: e.target.value,
                            emailError: "",
                          })
                        }
                        placeholder="exemplo@email.com"
                        disabled={sending}
                      />
                      {fornecedor.emailError && (
                        <label className="label">
                          <span className="label-text-alt text-error">
                            {fornecedor.emailError}
                          </span>
                        </label>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end items-center gap-2 border-t border-base-300 pt-4 mt-6">
        {totalReenvios > 0 && (
          <span className="text-xs text-warning mr-auto">
            {totalReenvios} reenvio(s) — cotações já recebidas serão reabertas
          </span>
        )}
        <button className="btn btn-ghost" onClick={onClose} disabled={sending}>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={
            sending ||
            selecionados.length === 0 ||
            selecionados.some(
              (f) =>
                !f.email.trim() ||
                !!f.emailError ||
                itensDoModo(f).length === 0,
            )
          }
        >
          {sending ? (
            <>
              <span className="loading loading-spinner"></span>
              Enviando...
            </>
          ) : (
            <>
              <HiPaperAirplane className="h-4 w-4" />
              Enviar para {selecionados.length} fornecedor(es)
            </>
          )}
        </button>
      </div>
    </ModalBase>
  );
};

export default ModalEnvioFornecedor;
